import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { optimizeUrl, optimizeSrcSet } from "@/lib/img";
import { Button } from "@/components/ui/button";
import { Trophy, Calendar, MapPin, Clock, Sparkles, ArrowRight, Award } from "lucide-react";

/**
 * Premium hero card shown at the top of user dashboards.
 * Displays the currently featured contest OR event (resolved per-audience by backend).
 * Auto-hides if nothing is active / set.
 */
export default function DashboardHero() {
  const [hero, setHero] = useState(undefined); // undefined=loading, null=none, object=present

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard-hero")
      .then(({ data }) => { if (!cancelled) setHero(data || null); })
      .catch(() => { if (!cancelled) setHero(null); });
    return () => { cancelled = true; };
  }, []);

  if (hero === undefined) {
    // Skeleton
    return (
      <div className="rounded-3xl bg-[#1A1A1A]/5 border border-[#EADDC9] h-[280px] sm:h-[320px] mb-8 animate-pulse" data-testid="dashboard-hero-skeleton" />
    );
  }
  if (!hero || !hero.target) return null;

  const isContest = hero.kind === "contest";
  const t = hero.target || {};

  // Resolve image with priority
  const bannerImage =
    t.banner_image ||
    t.cover_image ||
    t.image_url ||
    (t.cover_image_id && `${API}/img/${t.cover_image_id}?w=1600&q=86`) ||
    null;

  // Resolve metadata
  const description = (t.description || "").trim();
  const shortDesc = description.length > 180 ? description.slice(0, 177) + "…" : description;
  const reward = t.reward || t.prize || t.prize_label || null;
  const deadline = isContest ? t.end_date : t.date;
  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch { return iso; }
  };
  const time = !isContest ? t.time : null;
  const location = !isContest ? t.location : null;

  const detailHref = isContest ? `/concours/${t.id}` : `/events/${t.id}`;
  const ctaLabel = isContest ? "Participer au concours" : "Participer à l'événement";
  const badgeLabel = isContest ? "Concours actif" : "Événement à venir";
  const BadgeIcon = isContest ? Trophy : Calendar;

  return (
    <article
      className="relative rounded-3xl overflow-hidden mb-8 shadow-[0_24px_56px_-20px_rgba(110,75,42,0.45)] border border-[#1A1A1A]/10"
      data-testid="dashboard-hero"
      data-hero-kind={hero.kind}
    >
      {/* Top: image / fallback gradient */}
      <div className="relative h-44 sm:h-56 md:h-64 overflow-hidden bg-gradient-to-br from-[#C68E59] via-[#A26F3E] to-[#6E4B2A]">
        {bannerImage && (
          <img
            src={optimizeUrl(bannerImage, 1024)}
            srcSet={optimizeSrcSet(bannerImage, [480, 800, 1280, 1600])}
            sizes="(max-width: 640px) 100vw, 900px"
            alt={t.title || ""}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            data-testid="dashboard-hero-image"
          />
        )}
        {/* subtle dark gradient overlay for legibility on the inner badge */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-black/15 to-black/45 pointer-events-none" />
        {/* Featured badge */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1A1A1A] text-[#E6C29A] text-[11px] font-semibold tracking-[0.18em] uppercase backdrop-blur"
            data-testid="hero-badge-featured"
          >
            <Sparkles className="w-3.5 h-3.5" /> Mis en avant
          </span>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-[#1A1A1A] text-[11px] font-semibold tracking-[0.18em] uppercase"
            data-testid={`hero-badge-${hero.kind}`}
          >
            <BadgeIcon className="w-3.5 h-3.5" /> {badgeLabel}
          </span>
        </div>
      </div>

      {/* Bottom: dark premium panel */}
      <div className="bg-[#1A1A1A] text-white px-6 sm:px-8 py-7 sm:py-8 relative">
        {/* Caramel accent strip */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C68E59] to-transparent opacity-60" />

        <p className="caption-label !text-[#E6C29A]">
          {isContest ? "Concours Helencia" : "Événement Helencia"}
        </p>
        <h2
          className="font-serif text-3xl sm:text-4xl md:text-5xl mt-1 leading-[1.05] tracking-tight"
          data-testid="dashboard-hero-title"
        >
          {t.title || "Sans titre"}
        </h2>

        {shortDesc && (
          <p className="text-sm sm:text-base text-white/75 mt-3 leading-relaxed max-w-2xl" data-testid="dashboard-hero-desc">
            {shortDesc}
          </p>
        )}

        {/* Metadata row */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3" data-testid="dashboard-hero-meta">
          {isContest && reward && (
            <Meta icon={Award} label="Récompense" value={reward} />
          )}
          {deadline && (
            <Meta
              icon={Calendar}
              label={isContest ? "Deadline" : "Date"}
              value={fmtDate(deadline)}
            />
          )}
          {time && <Meta icon={Clock} label="Heure" value={time} />}
          {location && <Meta icon={MapPin} label="Lieu" value={location} />}
        </div>

        <div className="mt-7 flex items-center gap-3">
          <Link to={detailHref}>
            <Button
              className="rounded-full bg-[#C68E59] hover:bg-[#E6C29A] hover:text-[#1A1A1A] text-white px-7 h-12 font-semibold tracking-wide group"
              data-testid="dashboard-hero-cta"
            >
              {ctaLabel}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
          {hero.auto && (
            <span className="text-[10px] text-white/40 uppercase tracking-wider" data-testid="hero-auto-tag">
              Sélection automatique
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 border border-white/10">
        <Icon className="w-4 h-4 text-[#E6C29A]" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">{label}</p>
        <p className="text-sm text-white font-medium truncate">{value}</p>
      </div>
    </div>
  );
}
